import PropTypes from 'prop-types';

function ProgressBar({ currentStep, totalSteps, onStepClick = null }) {
  const steps = [
    { number: 1, label: 'Collecte données', shortLabel: 'Données' },
    { number: 2, label: 'Résultats financiers', shortLabel: 'Résultats' },
    { number: 3, label: 'Protection sociale', shortLabel: 'Protection' },
    { number: 4, label: 'Action', shortLabel: 'Action' },
  ];

  const progressPercentage = ((currentStep - 1) / (totalSteps - 1)) * 100;

  return (
    <div 
      className="bg-white border-b border-gray-200"
      role="navigation"
      aria-label="Progression de la simulation"
    >
      <div className="container mx-auto px-4 py-3 max-w-6xl">
        {/* Barre de progression visuelle */}
        <div className="relative">
          {/* Ligne de fond */}
          <div className="absolute top-5 left-0 right-0 h-1 bg-gray-200 rounded-full" />
          
          {/* Ligne de progression */}
          <div
            className="absolute top-5 left-0 h-1 bg-primary-500 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progressPercentage}%` }}
            role="progressbar"
            aria-valuenow={currentStep}
            aria-valuemin="1"
            aria-valuemax={totalSteps}
            aria-label={`Progression: étape ${currentStep} sur ${totalSteps}`}
          />

          {/* Étapes cliquables */}
          <div className="relative flex justify-between">
            {steps.map((step) => {
              const isCompleted = step.number < currentStep;
              const isCurrent = step.number === currentStep;
              const isAccessible = step.number <= currentStep;

              return (
                <button
                  key={step.number}
                  onClick={() => isAccessible && onStepClick && onStepClick(step.number)}
                  disabled={!isAccessible}
                  className={`
                    flex flex-col items-center gap-2 group relative
                    ${isAccessible ? 'cursor-pointer' : 'cursor-not-allowed'}
                    ${!isAccessible && 'opacity-50'}
                  `}
                  aria-label={`${step.label} - Étape ${step.number}${isCurrent ? ' (actuelle)' : ''}${isCompleted ? ' (terminée)' : ''}`}
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  {/* Cercle de l'étape */}
                  <div
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm
                      transition-all duration-300 border-2
                      ${
                        isCurrent
                          ? 'bg-primary-500 border-primary-500 text-white scale-110 shadow-lg'
                          : isCompleted
                          ? 'bg-primary-100 border-primary-500 text-primary-700'
                          : 'bg-white border-gray-300 text-gray-400'
                      }
                      ${isAccessible && !isCurrent && 'hover:scale-105 hover:shadow-md'}
                    `}
                  >
                    {isCompleted ? (
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      step.number
                    )}
                  </div>

                  {/* Label de l'étape */}
                  <span
                    className={`
                      text-xs font-medium text-center max-w-[80px]
                      transition-colors duration-300
                      ${
                        isCurrent
                          ? 'text-primary-700'
                          : isCompleted
                          ? 'text-gray-700'
                          : 'text-gray-400'
                      }
                    `}
                  >
                    {/* Afficher le label court sur mobile, complet sur desktop */}
                    <span className="hidden md:inline">{step.label}</span>
                    <span className="md:hidden">{step.shortLabel}</span>
                  </span>

                  {/* Tooltip au survol (desktop uniquement) */}
                  {isAccessible && !isCurrent && (
                    <span className="hidden md:block absolute -bottom-12 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                      Revenir à cette étape
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Indicateur de progression textuel pour accessibilité */}
        <div className="sr-only" aria-live="polite" aria-atomic="true">
          Vous êtes à l'étape {currentStep} sur {totalSteps}: {steps[currentStep - 1]?.label}
        </div>
      </div>
    </div>
  );
}

ProgressBar.propTypes = {
  currentStep: PropTypes.number.isRequired,
  totalSteps: PropTypes.number.isRequired,
  onStepClick: PropTypes.func,
};

export default ProgressBar;
